import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import {
  getVendorDetails,
  updateVendor,
  clearError,
  clearSuccess,
  clearVendorDetails,
} from "../../../../redux/Slices/authSlice";
import { toast } from "react-toastify";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";
import {
  saveVendorsCache,
  getVendorsCache,
  generateVendorsCacheKey,
  getAllVendorsCacheForQuery,
  isOnline,
} from "../../../../shared/utils.js/offlineQueue";
import { applyFieldFilter } from "../../../../shared/utils.js/fieldValidation";

const EditVendor = () => {
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { vendorDetails, isLoading, error, success } = useSelector(
    state => state.auth,
  );
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const isRTL = i18n.language === "ar";
  const [formMaxWidth, setFormMaxWidth] = useState("700px");

  const [formData, setFormData] = useState({
    name_en: "",
    name_ar: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    tax_number: "",
  });

  // Sidebar responsive effect
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar =
        document.querySelector("[data-sidebar]") ||
        document.querySelector(".sidebar") ||
        document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed =
          sidebar.classList.contains("collapsed") ||
          sidebar.getAttribute("data-collapsed") === "true" ||
          window.getComputedStyle(sidebar).width === "0px" ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty("--form-max-width", "100%");
          setFormMaxWidth("100%");
        } else {
          root.style.setProperty("--form-max-width", "750px");
          setFormMaxWidth("750px");
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar =
      document.querySelector("[data-sidebar]") ||
      document.querySelector(".sidebar") ||
      document.querySelector('[class*="sidebar"]');
    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ["class", "data-collapsed"],
      });
    }

    window.addEventListener("resize", updateFormWidth);
    window.addEventListener("sidebar-toggle", updateFormWidth);
    document.addEventListener("sidebar-collapsed", updateFormWidth);
    document.addEventListener("sidebar-expanded", updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateFormWidth);
      window.removeEventListener("sidebar-toggle", updateFormWidth);
      document.removeEventListener("sidebar-collapsed", updateFormWidth);
      document.removeEventListener("sidebar-expanded", updateFormWidth);
    };
  }, []);

  // useEffects
  useEffect(() => {
    if (id) {
      // ...existing code...
      // ...existing code...
      // ...existing code...

      // Try to load from cache first if offline
      if (!isOnline()) {
        // ...existing code...
        // Use per_page: 10 to match the saved cache keys
        getAllVendorsCacheForQuery({ per_page: 10, search: "" })
          .then(allCachedVendors => {
            // ...existing code...
            // ...existing code...
            const cachedVendor = allCachedVendors.find(
              v => v.id === Number(id),
            );
            // ...existing code...

            if (cachedVendor) {
              const details = cachedVendor;
              setFormData({
                name_en: details.name_en || details.name || "",
                name_ar: details.name_ar || details.name || "",
                contact_person: details.contact_person || "",
                email: details.email || "",
                phone: details.phone || "",
                address: details.address || "",
                tax_number: details.tax_number || "",
              });
              toast.info(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً."
                  : "No internet connection. Showing locally saved data.",
                { rtl: isRTL, autoClose: 3000 },
              );
            } else {
              // ...existing code...
              toast.warning(
                isRTL
                  ? "لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة لهذا المورد."
                  : "No internet connection and no cached data available for this vendor.",
                { rtl: isRTL, autoClose: 3000 },
              );
            }
          })
          .catch(err => {
            console.error("Error loading vendor from cache:", err);
          });
      } else {
        // ...existing code...
        dispatch(getVendorDetails(id));
      }
    }
    return () => {
      dispatch(clearVendorDetails());
    };
  }, [dispatch, id, isRTL]);

  useEffect(() => {
    if (vendorDetails) {
      const details = vendorDetails.data || vendorDetails;
      // ...existing code...

      setFormData({
        name_en: details.name_en || details.name || "",
        name_ar: details.name_ar || details.name || "",
        contact_person: details.contact_person || "",
        email: details.email || "",
        phone: details.phone || "",
        address: details.address || "",
        tax_number: details.tax_number || "",
      });

      // Save vendor details to cache for offline access
      if (details.id) {
        // ...existing code...
        // Use per_page: 10 to match reading parameters
        getAllVendorsCacheForQuery({ per_page: 10, search: "" })
          .then(allCachedVendors => {
            // ...existing code...

            const vendorIndex = allCachedVendors.findIndex(
              v => v.id === details.id,
            );
            let updatedVendors;

            if (vendorIndex !== -1) {
              // Update existing vendor in cache
              updatedVendors = [...allCachedVendors];
              updatedVendors[vendorIndex] = {
                ...updatedVendors[vendorIndex],
                ...details,
              };
            } else {
              // Add new vendor to cache
              updatedVendors = [...allCachedVendors, details];
            }

            // Save to multiple cache keys for different pages
            const perPage = 10;
            const totalPages = Math.ceil(updatedVendors.length / perPage);

            for (let page = 1; page <= totalPages; page++) {
              const startIndex = (page - 1) * perPage;
              const pageVendors = updatedVendors.slice(
                startIndex,
                startIndex + perPage,
              );
              const cacheKey = generateVendorsCacheKey({
                page,
                per_page: perPage,
                search: "",
              });

              saveVendorsCache(cacheKey, pageVendors, {
                current_page: page,
                last_page: totalPages,
                per_page: perPage,
                total: updatedVendors.length,
              })
                .then(() => {})
                .catch(err => {});
            }
          })
          .catch(err => {});
      }
    }
  }, [vendorDetails]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate("/vendors");
      }, 1500);
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    const finalValue =
      type === "checkbox" ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isOnline()) {
      try {
        await makeOfflineRequest(`/vendors/${id}`, "PUT", formData);
      } catch (err) {
        if (err.message === "OFFLINE_REQUEST_SAVED" || err.isOffline) {
          // Optimistically update cache
          try {
            const allCached = await getAllVendorsCacheForQuery({
              per_page: 10,
              search: "",
            });
            let updatedList = [...allCached];
            const index = updatedList.findIndex(v => v.id === Number(id));

            const optimisticVendor = {
              ...(index !== -1 ? updatedList[index] : {}),
              id: Number(id),
              ...formData,
              name: formData.name_en,
            };

            if (index !== -1) {
              updatedList[index] = optimisticVendor;
            } else {
              updatedList.push(optimisticVendor);
            }

            const perPage = 10;
            const totalPages = Math.ceil(updatedList.length / perPage);
            for (let page = 1; page <= totalPages; page++) {
              const startIndex = (page - 1) * perPage;
              const pageVendors = updatedList.slice(
                startIndex,
                startIndex + perPage,
              );
              const cacheKey = generateVendorsCacheKey({
                page,
                per_page: perPage,
                search: "",
              });
              await saveVendorsCache(cacheKey, pageVendors, {
                current_page: page,
                last_page: totalPages,
                per_page: perPage,
                total: updatedList.length,
              });
            }

            toast.info(
              isRTL
                ? "تم حفظ التعديلات محلياً وسيتم مزامنتها عند عودة الاتصال"
                : "Changes saved locally and will sync when online",
              { rtl: isRTL },
            );
            setTimeout(() => navigate("/vendors"), 1500);
          } catch (cacheErr) {
            console.error("Optimistic cache update failed", cacheErr);
            toast.error(
              isRTL ? "فشل تحديث الكاش المحلي" : "Failed to update local cache",
              { rtl: isRTL },
            );
          }
          return;
        }
      }
    }

    try {
      await dispatch(updateVendor({ id, data: formData }));
      // Success and error handling is done by useEffect
    } catch (error) {
      // Error handling is done by useEffect
    }
  };

  if (isLoading && !vendorDetails) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {t("vendors.edit.loading")}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          marginBottom: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: formMaxWidth,
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          {t("vendors.edit.title")}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/vendors")}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "10px 18px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {t("btns.back", { defaultValue: isRTL ? "رجوع" : "Back" })}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "32px",
          width: "100%",
          maxWidth: formMaxWidth,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          {isRTL ? (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.edit.nameAr")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "rtl",
                    textAlign: "right",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.edit.nameEn")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "ltr",
                    textAlign: "left",
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.edit.nameEn")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "ltr",
                    textAlign: "left",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.edit.nameAr")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "rtl",
                    textAlign: "right",
                  }}
                />
              </div>
            </>
          )}

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.edit.contactPerson")}
            </label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.edit.email")}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: "ltr",
                textAlign: "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.edit.phone")}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.edit.taxNumber")}
            </label>
            <input
              type="text"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div style={{ gridColumn: "1 / span 2" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.edit.address")}
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginTop: "24px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/vendors")}
            style={{
              padding: "12px 24px",
              backgroundColor: "#333",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {t("vendors.edit.cancel")}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: "12px 24px",
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? t("vendors.edit.saving") : t("vendors.edit.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditVendor;
